<template>
  <div
    v-loading="isFetchingDetails || getInTouchInProgress"
    class="partnership-details"
  >
    <div class="image-wrapper">
      <img
        class="card-img-top"
        :src="partnership.logo_url | cld({ h: 120, c: 'limit' })"
        :alt="partnership.name"
      />
    </div>
    <h1 class="title">
      <a
        v-if="partnership.website_url"
        :href="partnership.website_url"
        target="_blank"
      >
        <span>{{ partnership.name }}</span>
        <img
          src="/static/images/icon-external-link-alt.svg"
          alt="External link icon"
        />
      </a>
      <span v-else>{{ partnership.name }}</span>
    </h1>
    <div class="tags">
      <el-tag
        v-for="tag in partnership.tags"
        :key="tag"
        size="small"
        type="info"
      >
        {{ tag }}
      </el-tag>
    </div>
    <div class="description" v-html="partnership.description" />
    <div class="action-block">
      <template
        v-if="
          partnership.type === 'PROMO' &&
          partnership.has_promo_link_url &&
          !partnership.has_promo_code
        "
      >
        <el-button type="primary" @click="openPartnerUrl()">
          {{ partnership.promo_link_url_text || DEFAULT_PROMO_LINK_URL_TEXT }}
        </el-button>
      </template>
      <template
        v-if="
          partnership.type === 'PROMO' &&
          partnership.has_promo_link_url &&
          partnership.has_promo_code
        "
      >
        <div v-show="detailsFetched">
          <hr />
          <el-steps :active="step" align-center finish-status="success">
            <el-step title="Step 1" escription="Copy Coupon" />
            <el-step title="Step 2" escription="Start Registration" />
            <el-step title="Step 3" escription="Finish using coupon" />
          </el-steps>

          <div v-if="step === 0">
            <p class="hint">
              First copy a coupon (simply click on it).
            </p>
            <div class="promo-code-block">
              <CopyClick
                :text="promoCode"
                placement="right"
                @click.native="couponCopied = true"
              >
                <span class="promo-code">{{ promoCode }}</span>
              </CopyClick>
              <div class="bottom-buttons">
                <el-button
                  type="primary"
                  :disabled="!couponCopied"
                  @click="step += 1"
                >
                  Next
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="step === 1">
          <p class="hint">
            Now register at partner's page by visiting link below
          </p>

          <div class="bottom-buttons">
            <el-button type="text" class="back-button" @click="step -= 1">
              Back
            </el-button>
            <el-button type="primary" @click="openPartnerUrl()">
              Open page
            </el-button>
          </div>
        </div>

        <div v-if="step === 2">
          <p class="hint">
            Finish the process at our partner's page.
          </p>
          <div class="bottom-buttons">
            <el-button type="text" class="back-button" @click="step -= 1">
              Back
            </el-button>
            <el-button type="primary" @click="done()">
              Done
            </el-button>
          </div>
        </div>

        <el-button
          v-if="!detailsFetched"
          class="action-button"
          type="primary"
          @click="showCode()"
        >
          {{ partnership.promo_link_url_text || DEFAULT_PROMO_CODE }}
        </el-button>
      </template>

      <template v-if="partnership.type === 'MAIL_INTRO'">
        <el-button type="primary" @click="getInTouch()">
          {{ partnership.mail_intro_text || DEFAULT_MAIL_INTRO_TEXT }}
        </el-button>
      </template>
    </div>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import http from '@/services/http'
import { getDetails } from '@/services/marketplace-partnerships'
import CopyClick from 'components/CopyClick'
import {
  DEFAULT_PROMO_LINK_URL_TEXT,
  DEFAULT_PROMO_CODE,
  DEFAULT_MAIL_INTRO_TEXT,
} from 'services/marketplace-partnerships'
import { track } from '@/utils/analytics'

export default {
  components: {
    CopyClick,
  },
  props: {
    partnership: { type: Object, default: () => ({}) },
  },
  setup(props, { emit, root: { $confirm, $message } }) {
    const isFetchingDetails = ref(false)
    const detailsData = ref({})

    const trackDealButtonClick = (category = 'Marketplace: Deal button') => {
      track.click({
        category: category,
        label: props.partnership.name,
      })
    }

    const getPartnershipDetails = async () => {
      isFetchingDetails.value = true
      const request = getDetails(props.partnership.id)
      await http.sleep(1500)
      const response = await request
      isFetchingDetails.value = false
      return response.data
    }

    const openPartnerUrl = async () => {
      if (!detailsData.value.promo_link_url) {
        detailsData.value = await getPartnershipDetails()
      }
      window.open(detailsData.value.promo_link_url)
      trackDealButtonClick()
      step.value += 1
    }

    const promoCode = ref('')
    const detailsFetched = ref(false)
    const showCode = async () => {
      detailsData.value = await getPartnershipDetails()
      promoCode.value = detailsData.value.promo_code
      detailsFetched.value = true

      trackDealButtonClick()
    }
    const couponCopied = ref(false)

    const step = ref(0)

    const done = () => {
      emit('done')
    }

    const getInTouchInProgress = ref(false)
    const getInTouch = () => {
      $confirm(
        `Getting ${props.partnership.name} deal requires the email introduction.`,
        'Introduction confirmation',
        {
          type: 'info',
          confirmButtonText: 'Get Your Deal',
          cancelButtonText: 'Cancel',
        },
      )
        .then(async () => {
          // TODO: send API call to make a connection
          getInTouchInProgress.value = true
          await http.postAuthed(
            `/marketplace/partnerships/${props.partnership.id}/connect`,
          )
          await http.sleep(1000)
          trackDealButtonClick('Marketplace: Mail intro Deal button')
          getInTouchInProgress.value = false
          $message.success(
            'Done! We are going connect you with our partner via e-mail',
          )
          emit('cancel')
        })
        .catch(() => {})
    }

    return {
      DEFAULT_PROMO_LINK_URL_TEXT,
      DEFAULT_PROMO_CODE,
      DEFAULT_MAIL_INTRO_TEXT,
      openPartnerUrl,

      promoCode,
      isFetchingDetails,
      detailsFetched,
      showCode,
      couponCopied,
      step,

      done,

      getInTouchInProgress,
      getInTouch,
    }
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.partnership-details {
  .image-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 120px;

    img {
      max-width: 300px;
    }
  }

  .title {
    font-size: 16px;
    font-weight: 600;
    color: $grey90;

    a {
      display: inline-flex;
      align-items: center;
      text-decoration: underline;

      span {
        flex-shrink: 0;
      }

      img {
        margin-left: 6px;
        max-height: 14px;
        margin-top: -2px;
      }
    }
  }

  .tags {
    margin-bottom: 10px;
    height: 26px;
  }

  .website-wrapper {
    display: inline-block;
    color: $grey90;

    .website-link {
      font-size: 14px;
      margin-bottom: 10px;
      color: $grey90;
      ion-icon {
        color: $grey90;
      }
      &:hover {
        text-decoration: none;
        opacity: 0.8;
      }
    }
  }

  .description {
    font-size: 14px;
    font-weight: 400;
    color: $grey90;
    text-align: justify;
    span {
      display: inline-block;
    }
  }

  .action-block {
    text-align: center;
    margin-top: 40px;
    position: relative;

    .promo-code-block {
      .promo-code {
        font-size: 20px;
        color: $grey90;
        letter-spacing: 2px;
      }
    }

    .el-steps {
      margin-top: 20px;
      margin-bottom: 30px;
    }

    .action-button {
      margin-top: 20px;
      min-width: 200px;
    }

    .bottom-buttons {
      margin-top: 20px;
    }

    .back-button {
      position: absolute;
      left: 10px;
      bottom: 0px;
      color: $grey90;
    }

    .hint {
      color: $grey90;
      margin: 10px auto 10px;
    }
  }

  .el-form-item__content .vb-text-editor {
    line-height: 24px;
  }

  .el-form-item[faded] {
    opacity: 0.25;
  }
}
</style>
